import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Divider,
  Alert,
  Chip,
  Stack,
} from "@mui/material";

import AssessmentIcon from "@mui/icons-material/Assessment";
import DownloadIcon from "@mui/icons-material/Download";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SummarizeIcon from "@mui/icons-material/Summarize";
import TableViewIcon from "@mui/icons-material/TableView";

import * as XLSX from "xlsx";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "./firebase/firebaseConfig";
import { useAuth } from "./authContext";

/** helpers */
const norm = (s) =>
  (s ?? "").toString().trim().toLowerCase().replace(/\s+/g, " ");

const idNorm = (s) => norm(s).replace(/[^a-z0-9]/g, "");

const inRangeISO = (iso, fromISO, toISO) => {
  // dueDate stored as "YYYY-MM-DD" (string) in your tasks
  if (!iso) return false;
  if (fromISO && iso < fromISO) return false;
  if (toISO && iso > toISO) return false;
  return true;
};

const normStatusKey = (statusRaw) => {
  const n = norm(statusRaw);
  if (!n) return "Pending";
  if (["completed", "done"].includes(n)) return "Completed";
  if (["in progress", "in-progress", "progress", "ongoing"].includes(n))
    return "In Progress";
  if (["not applicable", "na", "n/a"].includes(n)) return "Not Applicable";
  if (["pending", "to do", "todo", "not started"].includes(n)) return "Pending";

  // if your db already stores canonical labels, keep them:
  if (statusRaw === "Completed") return "Completed";
  if (statusRaw === "In Progress") return "In Progress";
  if (statusRaw === "Pending") return "Pending";
  if (statusRaw === "Not Applicable") return "Not Applicable";

  return "Pending";
};

const safeName = (u) => u?.name || u?.displayName || u?.email || "";

/**
 * ReportExportPage
 * - Main download: Tasks export (filtered)
 * - Additional downloads:
 *    (A) Project Task Status (ProjectInformationPage style aggregation)
 *    (B) User Task Summary (UserTaskSummaryPage style aggregation)
 */
export default function ReportExportPage() {
  const { companyId, user, loading } = useAuth();

  // Base lists (org restricted)
  const [entities, setEntities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);

  // Filters
  const [selectedEntity, setSelectedEntity] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedUserType, setSelectedUserType] = useState("");
  const [selectedUser, setSelectedUser] = useState("");

  // Date range (dueDate)
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const myRole = user?.role || "Member"; // "Admin" / "Manager" / "Member"

  /** -----------------------------
   * Fetch org-scoped data
   * ----------------------------- */
  useEffect(() => {
    if (!companyId || !user) return;

    const run = async () => {
      setError("");
      try {
        const entSnap = await getDocs(
          query(collection(db, "entities"), where("companyId", "==", companyId))
        );
        setEntities(entSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

        const projSnap = await getDocs(
          query(collection(db, "projects"), where("companyId", "==", companyId))
        );
        setProjects(projSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

        const usrSnap = await getDocs(
          query(collection(db, "users"), where("companyId", "==", companyId))
        );
        setUsers(usrSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

        const taskSnap = await getDocs(
          query(collection(db, "tasks"), where("companyId", "==", companyId))
        );
        setTasks(taskSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error(e);
        setError("Failed to load report data.");
      }
    };

    run();
  }, [companyId, user]);

  /** -----------------------------
   * Lookup maps (names, roles, project meta)
   * ----------------------------- */
  const userByUid = useMemo(() => {
    const m = new Map();
    (users || []).forEach((u) => {
      const key = u.uid || u.id;
      if (key) m.set(key, u);
    });
    return m;
  }, [users]);

  const userByNameOrEmail = useMemo(() => {
    const m = new Map();
    (users || []).forEach((u) => {
      const n = idNorm(u.name);
      const e = idNorm(u.email);
      if (n) m.set(n, u);
      if (e) m.set(e, u);
    });
    return m;
  }, [users]);

  const projectByName = useMemo(() => {
    const m = new Map();
    (projects || []).forEach((p) => m.set(norm(p.name), p));
    return m;
  }, [projects]);

  const getTaskProject = (t) => projectByName.get(norm(t.project)) || null;

  const getTaskProjectCategory = (t) => getTaskProject(t)?.category || "";

  const getTaskManagerId = (t) =>
    t.managerId || getTaskProject(t)?.managerId || "";
  const getTaskTeamLeadId = (t) =>
    t.teamLeadId || getTaskProject(t)?.teamLeadId || "";

  const getTaskManagerName = (t) => {
    const p = getTaskProject(t) || {};
    const managerId = getTaskManagerId(t);
    const u = userByUid.get(managerId);
    return t.managerName || p.managerName || safeName(u) || "";
  };

  const getTaskTeamLeadName = (t) => {
    const p = getTaskProject(t) || {};
    const teamLeadId = getTaskTeamLeadId(t);
    const u = userByUid.get(teamLeadId);
    return t.teamLeadName || p.teamLeadName || safeName(u) || "";
  };

  /** -----------------------------
   * Role-scoped tasks (same visibility principle)
   * Admin: all org tasks
   * Manager: tasks under projects they manage (by managerId or managerName match)
   * Member: tasks assigned to them (assignedTo matches name/email)
   * ----------------------------- */
  const managedProjectKeys = useMemo(() => {
    if (myRole !== "Manager") return new Set();
    const uid = user?.uid || "";
    const myName = idNorm(user?.name);
    return new Set(
      (projects || [])
        .filter(
          (p) =>
            (p.managerId && p.managerId === uid) ||
            (p.managerName && idNorm(p.managerName) === myName)
        )
        .map((p) => norm(p.name))
    );
  }, [projects, myRole, user]);

  const roleScopedTasks = useMemo(() => {
    if (!tasks?.length) return [];

    if (myRole === "Admin") return tasks;

    if (myRole === "Manager") {
      const uid = user?.uid || "";
      return tasks.filter((t) => {
        const byManagerId = getTaskManagerId(t) === uid;
        const byManagedProject = managedProjectKeys.size
          ? managedProjectKeys.has(norm(t.project))
          : false;
        return byManagerId || byManagedProject;
      });
    }

    // Member
    const myName = idNorm(user?.name);
    const myEmail = idNorm(user?.email);
    return tasks.filter((t) => {
      const a = idNorm(t.assignedTo);
      return (myName && a === myName) || (myEmail && a === myEmail);
    });
  }, [tasks, myRole, user, managedProjectKeys, projectByName]); // projectByName via helpers

  /** -----------------------------
   * Cascading dropdown options
   * (each list only shows what the user can actually access)
   * ----------------------------- */

  const entityOptions = useMemo(() => {
    const allowedEntityNames = new Set(
      roleScopedTasks.map((t) => norm(t.entity)).filter(Boolean)
    );
    if (myRole === "Admin") return entities || [];
    return (entities || []).filter((e) =>
      allowedEntityNames.has(norm(e.name))
    );
  }, [entities, roleScopedTasks, myRole]);

  const baseVisibleProjects = useMemo(() => {
    const allowedProjectNames = new Set(
      roleScopedTasks.map((t) => norm(t.project)).filter(Boolean)
    );

    // Start from only those projects that appear in roleScopedTasks
    let base = (projects || []).filter((p) =>
      allowedProjectNames.has(norm(p.name))
    );

    // cascade by Entity
    if (selectedEntity) {
      const allowedFromEntity = new Set(
        roleScopedTasks
          .filter((t) => norm(t.entity) === norm(selectedEntity))
          .map((t) => norm(t.project))
          .filter(Boolean)
      );
      base = base.filter((p) => allowedFromEntity.has(norm(p.name)));
    }

    // cascade by Category
    if (selectedCategory) {
      base = base.filter((p) => norm(p.category) === norm(selectedCategory));
    }

    // sort
    return [...base].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      })
    );
  }, [projects, roleScopedTasks, selectedEntity, selectedCategory, myRole]);

  const categoryOptions = useMemo(() => {
    // categories only from visible projects
    const s = new Set(
      (baseVisibleProjects || []).map((p) => p.category).filter(Boolean)
    );
    return Array.from(s).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }, [baseVisibleProjects]);

  const projectOptions = useMemo(() => baseVisibleProjects, [baseVisibleProjects]);

  const userOptions = useMemo(() => {
    // Start from org users; optionally filter by user type
    let base = [...(users || [])];

    if (selectedUserType) {
      base = base.filter((u) => norm(u.role) === norm(selectedUserType));
    }

    // Restrict to only users who appear as AssignedTo in currently scoped tasks
    let scopedTasks = roleScopedTasks;

    if (selectedEntity)
      scopedTasks = scopedTasks.filter(
        (t) => norm(t.entity) === norm(selectedEntity)
      );
    if (selectedProject)
      scopedTasks = scopedTasks.filter(
        (t) => norm(t.project) === norm(selectedProject)
      );
    if (selectedCategory) {
      scopedTasks = scopedTasks.filter(
        (t) => norm(getTaskProjectCategory(t)) === norm(selectedCategory)
      );
    }
    if (fromDate || toDate)
      scopedTasks = scopedTasks.filter((t) =>
        inRangeISO(t.dueDate, fromDate, toDate)
      );

    const assignedToSet = new Set(
      scopedTasks.map((t) => idNorm(t.assignedTo)).filter(Boolean)
    );

    base = base.filter((u) => {
      const n = idNorm(u.name);
      const e = idNorm(u.email);
      return assignedToSet.has(n) || assignedToSet.has(e);
    });

    return [...base].sort((a, b) =>
      (safeName(a) || "").localeCompare(safeName(b) || "", undefined, {
        sensitivity: "base",
      })
    );
  }, [
    users,
    roleScopedTasks,
    selectedUserType,
    selectedEntity,
    selectedProject,
    selectedCategory,
    fromDate,
    toDate,
  ]);

  /** keep selections consistent when upstream filters change */
  useEffect(() => {
    if (
      selectedCategory &&
      !categoryOptions.some((c) => norm(c) === norm(selectedCategory))
    ) {
      setSelectedCategory("");
    }
  }, [categoryOptions, selectedCategory]);

  useEffect(() => {
    if (
      selectedProject &&
      !projectOptions.some((p) => norm(p.name) === norm(selectedProject))
    ) {
      setSelectedProject("");
    }
  }, [projectOptions, selectedProject]);

  useEffect(() => {
    if (
      selectedUser &&
      !userOptions.some((u) => (u.uid || u.id) === selectedUser)
    ) {
      setSelectedUser("");
    }
  }, [userOptions, selectedUser]);

  /** -----------------------------
   * Filtered task rows (the base for ALL exports)
   * ----------------------------- */
  const filteredRows = useMemo(() => {
    let rows = [...roleScopedTasks];

    if (selectedEntity)
      rows = rows.filter((r) => norm(r.entity) === norm(selectedEntity));
    if (selectedProject)
      rows = rows.filter((r) => norm(r.project) === norm(selectedProject));

    if (selectedCategory) {
      rows = rows.filter(
        (r) => norm(getTaskProjectCategory(r)) === norm(selectedCategory)
      );
    }

    if (fromDate || toDate) {
      rows = rows.filter((r) => inRangeISO(r.dueDate, fromDate, toDate));
    }

    if (selectedUser) {
      const u = users.find((x) => (x.uid || x.id) === selectedUser);
      const uname = idNorm(u?.name);
      const uemail = idNorm(u?.email);
      rows = rows.filter((r) => {
        const a = idNorm(r.assignedTo);
        return (uname && a === uname) || (uemail && a === uemail);
      });
    }

    // UserType filter: applied at assignee level using users list
    if (selectedUserType) {
      rows = rows.filter((r) => {
        const u = userByNameOrEmail.get(idNorm(r.assignedTo));
        return norm(u?.role) === norm(selectedUserType);
      });
    }

    rows.sort(
      (a, b) =>
        (a.dueDate || "").localeCompare(b.dueDate || "") ||
        (a.project || "").localeCompare(b.project || "")
    );
    return rows;
  }, [
    roleScopedTasks,
    selectedEntity,
    selectedProject,
    selectedCategory,
    fromDate,
    toDate,
    selectedUser,
    selectedUserType,
    users,
    userByNameOrEmail,
  ]);

  /** -----------------------------
   * Excel export: TASKS (existing main export)
   * ----------------------------- */
  const handleExportTasks = async () => {
    setBusy(true);
    setError("");
    try {
      if (!filteredRows.length) {
        setError("No data matches the selected filters.");
        return;
      }

      const exportRows = filteredRows.map((t) => ({
        Entity: t.entity || "",
        Project: t.project || "",
        ProjectCategory: getTaskProjectCategory(t) || "",
        Area: t.area || "",
        Task: t.name || "",
        AssignedTo: t.assignedTo || "",
        AssigneeRole: userByNameOrEmail.get(idNorm(t.assignedTo))?.role || "",
        Status: t.status || "",
        Priority: t.priority || "",
        DueDate: t.dueDate || "",
        CreatedBy: t.createdBy || "",
        ManagerName: getTaskManagerName(t) || "",
        TeamLeadName: getTaskTeamLeadName(t) || "",
        Reviewed: t.reviewed ? "Yes" : "No",
      }));

      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Tasks");

      const fnameParts = [
        "WorkPulse-TasksReport",
        selectedEntity ? `Entity-${selectedEntity}` : null,
        selectedProject ? `Project-${selectedProject}` : null,
        selectedCategory ? `Category-${selectedCategory}` : null,
        selectedUserType ? `UserType-${selectedUserType}` : null,
        fromDate ? `From-${fromDate}` : null,
        toDate ? `To-${toDate}` : null,
      ].filter(Boolean);

      XLSX.writeFile(wb, `${fnameParts.join("_")}.xlsx`);
    } catch (e) {
      console.error(e);
      setError("Error generating report. Please check console for details.");
    } finally {
      setBusy(false);
    }
  };

  /** -----------------------------
   * Excel export: PROJECT TASK STATUS (ProjectInformationPage style)
   * ----------------------------- */
  const handleExportProjectStatus = async () => {
    setBusy(true);
    setError("");
    try {
      if (!filteredRows.length) {
        setError("No data matches the selected filters.");
        return;
      }

      // group by entity + project
      const bucket = new Map();
      for (const t of filteredRows) {
        const entity = t?.entity || "Unknown";
        const project = t?.project || "Unnamed";
        const key = `${norm(entity)}|${norm(project)}`;

        if (!bucket.has(key)) {
          bucket.set(key, {
            Entity: entity,
            Project: project,
            ProjectCategory: getTaskProjectCategory(t) || "",
            ManagerName: getTaskManagerName(t) || "",
            TeamLeadName: getTaskTeamLeadName(t) || "",
            TotalTasks: 0,
            Pending: 0,
            "In Progress": 0,
            Completed: 0,
            "Not Applicable": 0,
            Reviewed: 0,
            CompletionPct: 0,
          });
        }

        const row = bucket.get(key);
        row.TotalTasks += 1;

        const sk = normStatusKey(t?.status);
        row[sk] = (row[sk] || 0) + 1;

        if (Boolean(t?.reviewed)) row.Reviewed = (row.Reviewed || 0) + 1;
      }

      const out = Array.from(bucket.values()).map((r) => {
        const done =
          Number(r.Completed || 0) + Number(r["Not Applicable"] || 0);
        const pct = r.TotalTasks ? Math.round((done / r.TotalTasks) * 100) : 0;
        return { ...r, CompletionPct: pct };
      });

      out.sort(
        (a, b) =>
          (a.Entity || "").localeCompare(b.Entity || "", undefined, {
            sensitivity: "base",
          }) ||
          (a.Project || "").localeCompare(b.Project || "", undefined, {
            sensitivity: "base",
          })
      );

      const ws = XLSX.utils.json_to_sheet(out);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "ProjectStatus");

      const fnameParts = [
        "WorkPulse-ProjectStatus",
        selectedEntity ? `Entity-${selectedEntity}` : null,
        selectedProject ? `Project-${selectedProject}` : null,
        selectedCategory ? `Category-${selectedCategory}` : null,
        selectedUserType ? `UserType-${selectedUserType}` : null,
        fromDate ? `From-${fromDate}` : null,
        toDate ? `To-${toDate}` : null,
      ].filter(Boolean);

      XLSX.writeFile(wb, `${fnameParts.join("_")}.xlsx`);
    } catch (e) {
      console.error(e);
      setError("Error generating project status export. Please check console.");
    } finally {
      setBusy(false);
    }
  };

  /** -----------------------------
   * Excel export: USER TASK SUMMARY (UserTaskSummaryPage style)
   * ----------------------------- */
  const handleExportUserTaskSummary = async () => {
    setBusy(true);
    setError("");
    try {
      if (!filteredRows.length) {
        setError("No data matches the selected filters.");
        return;
      }

      // group by entity + assignedTo + project
      const bucket = new Map();
      for (const t of filteredRows) {
        const entity = t?.entity || "Unknown";
        const assignee = t?.assignedTo || "Unassigned";
        const project = t?.project || "Unnamed";
        const key = `${norm(entity)}|${idNorm(assignee)}|${norm(project)}`;

        if (!bucket.has(key)) {
          const u = userByNameOrEmail.get(idNorm(assignee));
          bucket.set(key, {
            Entity: entity,
            User: assignee,
            UserType: u?.role || "",
            Project: project,
            ProjectCategory: getTaskProjectCategory(t) || "",
            ManagerName: getTaskManagerName(t) || "",
            TeamLeadName: getTaskTeamLeadName(t) || "",
            TotalTasks: 0,
            Pending: 0,
            "In Progress": 0,
            Completed: 0,
            "Not Applicable": 0,
            Reviewed: 0,
            CompletionPct: 0,
          });
        }

        const row = bucket.get(key);
        row.TotalTasks += 1;

        const sk = normStatusKey(t?.status);
        row[sk] = (row[sk] || 0) + 1;

        if (Boolean(t?.reviewed)) row.Reviewed = (row.Reviewed || 0) + 1;
      }

      const out = Array.from(bucket.values()).map((r) => {
        const done =
          Number(r.Completed || 0) + Number(r["Not Applicable"] || 0);
        const pct = r.TotalTasks ? Math.round((done / r.TotalTasks) * 100) : 0;
        return { ...r, CompletionPct: pct };
      });

      out.sort(
        (a, b) =>
          (a.Entity || "").localeCompare(b.Entity || "", undefined, {
            sensitivity: "base",
          }) ||
          (a.User || "").localeCompare(b.User || "", undefined, {
            sensitivity: "base",
          }) ||
          (a.Project || "").localeCompare(b.Project || "", undefined, {
            sensitivity: "base",
          })
      );

      const ws = XLSX.utils.json_to_sheet(out);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "UserTaskSummary");

      const fnameParts = [
        "WorkPulse-UserTaskSummary",
        selectedEntity ? `Entity-${selectedEntity}` : null,
        selectedProject ? `Project-${selectedProject}` : null,
        selectedCategory ? `Category-${selectedCategory}` : null,
        selectedUserType ? `UserType-${selectedUserType}` : null,
        fromDate ? `From-${fromDate}` : null,
        toDate ? `To-${toDate}` : null,
      ].filter(Boolean);

      XLSX.writeFile(wb, `${fnameParts.join("_")}.xlsx`);
    } catch (e) {
      console.error(e);
      setError("Error generating user task summary export. Please check console.");
    } finally {
      setBusy(false);
    }
  };

  const resetFilters = () => {
    setSelectedEntity("");
    setSelectedCategory("");
    setSelectedProject("");
    setSelectedUserType("");
    setSelectedUser("");
    setFromDate("");
    setToDate("");
    setError("");
  };

  if (loading) return <div>Loading...</div>;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: "1px solid rgba(0,0,0,.08)",
          boxShadow: "0 10px 30px rgba(0,0,0,.06)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
          <AssessmentIcon color="primary" />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              Report Export
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Export Excel reports based on your organisation + role access.
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Chip label={`Role: ${myRole}`} variant="outlined" />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          {/* Entity */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Entity</InputLabel>
              <Select
                label="Entity"
                value={selectedEntity}
                onChange={(e) => setSelectedEntity(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {entityOptions.map((e) => (
                  <MenuItem key={e.id} value={e.name || ""}>
                    {e.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Category */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Project Category</InputLabel>
              <Select
                label="Project Category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {categoryOptions.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Project */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Project</InputLabel>
              <Select
                label="Project"
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {projectOptions.map((p) => (
                  <MenuItem key={p.id} value={p.name || ""}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* User Type */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>User Type</InputLabel>
              <Select
                label="User Type"
                value={selectedUserType}
                onChange={(e) => setSelectedUserType(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Manager">Manager</MenuItem>
                <MenuItem value="Member">Member</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* User */}
          <Grid item xs={12} md={8}>
            <FormControl fullWidth>
              <InputLabel>User</InputLabel>
              <Select
                label="User"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {userOptions.map((u) => (
                  <MenuItem key={u.uid || u.id} value={u.uid || u.id}>
                    {safeName(u) || "User"} {u.role ? `(${u.role})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Dates */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="From Due Date"
              InputLabelProps={{ shrink: true }}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="To Due Date"
              InputLabelProps={{ shrink: true }}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* MAIN ACTIONS */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems="center"
        >
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={busy}
            onClick={handleExportTasks}
            sx={{ borderRadius: 2, px: 2.5 }}
          >
            {busy ? "Generating..." : "Generate & Download Tasks Excel"}
          </Button>

          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={resetFilters}
            sx={{ borderRadius: 2 }}
          >
            Reset Filters
          </Button>

          <Box sx={{ flex: 1 }} />

          <Chip label={`Rows: ${filteredRows.length}`} variant="outlined" />
        </Stack>


        {/* NEW SECTIONS */}
        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Additional Downloads
          </Typography>
          <Typography variant="body2" color="text.secondary">
            These exports mirror the “Project Information” and “User Task Summary”
            style reports using the same filters above.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2.2,
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,.08)",
                bgcolor: "rgba(0,0,0,.01)",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <SummarizeIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Project Task Status (Project Information)
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                One row per Project showing Total Tasks + status breakup
                (Pending / In Progress / Completed / NA), Reviewed count,
                Completion %, plus Project Category and Manager.
              </Typography>

              <Button
                fullWidth
                variant="contained"
                startIcon={<TableViewIcon />}
                disabled={busy}
                onClick={handleExportProjectStatus}
                sx={{ borderRadius: 2 }}
              >
                Download Project Status Excel
              </Button>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2.2,
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,.08)",
                bgcolor: "rgba(0,0,0,.01)",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <SummarizeIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  User Task Summary (User Task Summary)
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                One row per Entity + User + Project showing Total Tasks + status
                breakup, Reviewed count, Completion %, plus User Type, Project
                Category, Manager and Team Lead.
              </Typography>

              <Button
                fullWidth
                variant="contained"
                startIcon={<TableViewIcon />}
                disabled={busy}
                onClick={handleExportUserTaskSummary}
                sx={{ borderRadius: 2 }}
              >
                Download User Task Summary Excel
              </Button>
            </Paper>
          </Grid>
        </Grid>


        {/* INSTRUCTIONS (UI ONLY) - added below main button */}
        <Divider sx={{ mt: 3, mb: 2 }} />

        <Box
          sx={{
            p: 2.25,
            borderRadius: 3,
            border: "1px solid rgba(0,0,0,.08)",
            bgcolor: "rgba(0,0,0,.02)",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.75 }}>
            How to use this page
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This report page is <b>organisation-restricted</b> and <b>role-based</b>. You can export only the same level of
            data you can view in WorkPulse (Admin / Manager / Member).
            Filters automatically cascade, so each dropdown only shows options you are allowed to access.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
                Steps (recommended order)
              </Typography>
              <Box component="ol" sx={{ m: 0, pl: 2.4 }}>
                <li>
                  <Typography variant="body2">
                    Select <b>Entity</b> (optional). This restricts project/category/user lists to that entity.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Select <b>Project Category</b> (optional) to generate category-wise reports.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Select <b>Project</b> (optional) if you need a single project task listing or summary.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Select <b>User Type</b> and/or <b>User</b> (optional) for assignee-wise reports.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Choose <b>From Due Date</b> and <b>To Due Date</b> (optional) for weekly/monthly/period-based reporting.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Click one of the download buttons. The Excel file name includes key filters for easy identification.
                  </Typography>
                </li>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
                What reports you can generate from this page
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.4 }}>
                <li>
                  <Typography variant="body2">
                    <b>Tasks Excel (Detailed Listing):</b> Full task-level export (Entity, Project, Category, Task, Assigned To, Status,
                    Priority, Due Date, Manager/TeamLead, Reviewed).
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <b>Entity-wise Report:</b> Select Entity → download all tasks/summaries for that entity.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <b>Project-wise Report:</b> Select Project → download a focused report for a single project.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <b>Category-wise Report:</b> Select Project Category → download all tasks under that category across projects.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <b>User-wise / User Type-wise Report:</b> Select User or User Type → download assignment-wise reporting.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <b>Date-based Due Report:</b> Set From/To Due Date → export tasks falling due in that period.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <b>Highly-filtered Report:</b> Combine filters (e.g., Entity + Category + Date Range) to create a precise output.
                  </Typography>
                </li>
              </Box>

              <Box sx={{ mt: 1.25 }}>
                <Typography variant="caption" color="text.secondary">
                  Tip: If you want an “overall” report, keep filters as <b>All</b> and only set a date range.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

      </Paper>
    </Container>
  );
}
